import { useContext } from 'react';
import { Link } from 'react-router-dom';
import { DemoModeContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, ClipboardList, PlayCircle, MessageCircle, HelpCircle, Accessibility, LayoutPanelLeft } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
// 2026-06-08 (truth-lock §9.6): BuildStatusRibbon REMOVED from every surface.
// No engine count, no pytest count, no LLM count, no encounter count
// appears on any public or authenticated page. Admin dashboard retains its
// counter; nothing else surfaces these numbers anywhere.

// Session 4 (Part A) — "How to Use ?" maps to the footer information accordions.
const HOW_TO_ITEMS = [
  { id: 'footer-hallucinate', label: "Why P.Ai doesn't hallucinate" },
  { id: 'footer-instructions', label: 'Instructions to fill this form' },
  { id: 'footer-physicians', label: 'For Physicians & Providers' },
  { id: 'footer-interoperability', label: 'True Interoperability' },
];
function scrollToFooter(id) {
  const el = document.getElementById(id);
  if (!el) return;
  if (el.tagName === 'DETAILS') el.open = true;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export default function Header({ onRetrieve, onSoap, onStartIntake, onHistory, onAdmin, onAgent, onInvestor, onAmbientScribe, onDemoSummary }) {
  const { demoMode } = useContext(DemoModeContext);
  return (
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50" data-testid="main-header">
      <div className="max-w-6xl mx-auto px-3 sm:px-6 py-2.5">
        {/* Row 1: Logo LEFT | Badge CENTER | Start My Intake RIGHT */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5 flex-shrink-0">
            <img
              src="https://customer-assets.emergentagent.com/job_patient-ai-intake/artifacts/o1gistai_image.png"
              alt="P.AI Logo"
              className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0"
              style={{filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))'}}
            />
            <div className="flex flex-col leading-tight">
              <span className="text-base sm:text-xl font-extrabold tracking-tight">
                <span className="text-[#EF4444]" style={{textShadow: '1px 1px 0px rgba(239,68,68,0.15)'}}>P.AI</span><span className="text-slate-900"> Global Health</span>
              </span>
              <span className="text-[10px] sm:text-xs font-semibold text-slate-500 tracking-wide">(PatientAI — Cures Act Health Record Vault, Deterministically Evidence-Based)</span>
            </div>
          </div>

          <div className="flex items-center border-3 border-slate-900 rounded-full px-4 sm:px-6 py-1.5 sm:py-2 shadow-sm">
            <span className="text-sm sm:text-lg font-extrabold tracking-wider text-slate-900 uppercase whitespace-nowrap">
              Clinical AI Prototype · <span className="text-[#EF4444]">No PHI</span>
            </span>
          </div>

          <Button
            onClick={onStartIntake}
            size="sm"
            className="rounded-md text-[10px] sm:text-xs bg-[#2563EB] hover:bg-blue-700 text-white h-8 sm:h-9 px-2.5 sm:px-3 flex-shrink-0"
            data-testid="header-start-intake-btn"
          >
            <PlayCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1" /> <span className="hidden sm:inline">Start My </span>Intake
          </Button>
        </div>

        {/* Accessibility quick-link — Section 1557 / ADA-aware entry for Deaf / HOH / AAC users */}
        <div className="flex items-center justify-end mt-1">
          <Link
            to="/accessibility"
            className="inline-flex items-center gap-1.5 text-sm sm:text-base font-semibold text-violet-700 hover:text-violet-900"
            data-testid="header-accessibility-link"
          >
            <Accessibility className="w-4 h-4 sm:w-5 sm:h-5" /> Accessibility for Deaf / Hard-of-Hearing patients
          </Link>
        </div>

        {/* Row 2: 24/7 Assistant LEFT | Retrieve + SOAP RIGHT */}
        <div className="flex items-center justify-between gap-1.5 sm:gap-2 mt-1.5 pt-1.5 border-t border-slate-100">
          <Button
            onClick={onAgent}
            size="sm"
            className="rounded-md text-[10px] sm:text-xs bg-[#2563EB] hover:bg-blue-700 text-white h-8 sm:h-9 px-2 sm:px-3"
            data-testid="header-agent-btn"
          >
            <MessageCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1" />
            <span className="flex flex-col items-start leading-tight">
              <span>Patient 24/7 Virtual Assistant</span>
              <span className="text-[7px] sm:text-[9px] font-normal opacity-80">For help · Not for intake</span>
            </span>
          </Button>

          <div className="flex items-center gap-1.5 sm:gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-md text-[10px] sm:text-xs border-slate-300 text-slate-500 hover:bg-slate-50 h-7 sm:h-9 px-2 sm:px-3"
                  data-testid="header-howto-btn"
                >
                  <HelpCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1" /> How to Use ?
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                {HOW_TO_ITEMS.map((it) => (
                  <DropdownMenuItem
                    key={it.id}
                    onClick={() => scrollToFooter(it.id)}
                    data-testid={`howto-${it.id}`}
                    className="cursor-pointer text-sm"
                  >
                    {it.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              size="sm"
              onClick={onRetrieve}
              className="rounded-md text-[10px] sm:text-xs border-slate-300 text-slate-700 hover:bg-slate-50 h-7 sm:h-9 px-2 sm:px-3"
              data-testid="header-retrieve-btn"
            >
              <FileText className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1" />
              <span className="flex flex-col items-start leading-tight">
                <span>Retrieve your History</span>
                <span className="text-[7px] sm:text-[9px] text-slate-400 font-normal">Your Encounter ID</span>
              </span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onSoap}
              className="rounded-md text-[10px] sm:text-xs border-slate-300 text-slate-700 hover:bg-slate-50 h-7 sm:h-9 px-2 sm:px-3"
              data-testid="header-soap-btn"
            >
              <ClipboardList className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1" />
              <span className="flex flex-col items-start leading-tight">
                <span>Clinical SOAP Note</span>
                <span className="text-[7px] sm:text-[9px] text-slate-400 font-normal">For physician review</span>
              </span>
            </Button>
          </div>
        </div>

        {/* Demo Mode banner — appears only when demoMode is active. Loom-recording surface (May 26, 2026) */}
        {demoMode && (
          <div className="mt-2 -mx-3 sm:-mx-6 px-3 sm:px-6 py-2 bg-emerald-50 border-y-2 border-emerald-300 flex flex-wrap items-center justify-between gap-2" data-testid="demo-mode-banner">
            <div className="flex items-center gap-2" data-testid="demo-mode-active-badge">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-xs sm:text-sm font-bold text-emerald-800 uppercase tracking-wider">YC Demo Mode Active · mock data only</span>
            </div>
            <Button
              onClick={onDemoSummary}
              size="sm"
              className="rounded-md text-xs bg-slate-900 hover:bg-slate-700 text-white h-9 px-4 font-semibold"
              data-testid="header-demo-summary-btn"
            >
              <LayoutPanelLeft className="w-4 h-4 mr-2" />
              Open Patient + Physician View
            </Button>
          </div>
        )}
      </div>
    </header>
  );
}
